import { useEffect, useRef } from 'react';
import { useWorldStore } from '../state/worldStore';
import { useCityStore } from '../city/cityStore';
import { serverNow } from '../services/worldSocket';
import { api } from '../services/api';
import { ROOMS } from '../rooms';
import { normalizeVenue, registerVenues, registryKey, venueOpenAt } from '../city/venueRegistry';
import { subscribeBoot, bootState } from '../state/bootProgress';

// VenueRegistrySync — the venues the town voted for, kept current (plan:
// API docs/VENUE_PIPELINE_PLAN.md, step 5 "Live").
//
// Reads GET /api/world/venues at start-up, every two minutes, and whenever the
// living city's snapshot names a venue this World has not heard of (the API
// merges each registry venue into the city's places as `anchor: <slug>`, so a
// build shows up in `city_state` first). Each answer is written into ROOMS and
// TOWN_LOCATIONS in place (city/venueRegistry) and into the store, where
// CityModel, Portals, the facades and the wall screens pick it up.
//
// It also holds the CLOCK for them. The store cannot import the server clock
// (the socket module imports the store), so every few seconds this works out
// which venues are open now and publishes that — value-stable, so a quiet tick
// re-renders nothing. The door, the sign and enterInterior read it.
//
// LIVE-SAFE: a failed or missing endpoint keeps whatever was last known (on
// first load, nothing — the World exactly as it was). Only a successful answer
// can retire a venue.

const POLL_MS = 2 * 60 * 1000;
const TICK_MS = 10 * 1000;
const plain = (u) => String(u).split('?')[0];

export default function VenueRegistrySync() {
  const busy = useRef(false);
  const listRef = useRef([]);
  const entered = useRef(false);

  useEffect(() => {
    let dead = false;

    const tick = () => {
      const now = serverNow();
      const out = {};
      for (const v of listRef.current) {
        const open = venueOpenAt(v, now);
        out[v.slug] = { open, opensAt: open ? null : (v.opensAt != null && v.opensAt > now ? v.opensAt : null) };
      }
      useWorldStore.getState().setVenueOpen(out);
    };

    const load = async (why) => {
      if (busy.current) return;
      busy.current = true;
      try {
        const d = await api.worldVenues();
        if (dead) return;
        const seen = new Set();
        const all = (Array.isArray(d?.venues) ? d.venues : []).map(normalizeVenue)
          .filter((v) => v && !seen.has(v.slug) && seen.add(v.slug));
        const ws = useWorldStore.getState();
        const res = registerVenues(all, { bounds: ws.cityBounds });
        // A slug that names a built-in room is refused by registerVenues, and
        // must not reach the store either — CityModel would hand the Warehouse
        // somebody else's site. A venue of a type this World cannot build stays:
        // it has no room or door, but its anchor still puts its city place
        // (the artists standing outside it) where the server thinks it is.
        const list = all.filter((v) => !(ROOMS[v.slug] && !ROOMS[v.slug].registry));
        listRef.current = list;
        // Hosts and hours: what the walls and the door need, kept apart from the
        // key that re-measures the city. The media are compared without their
        // signatures — the asset proxy re-signs by bucket and name anyway.
        const live = list.map((v) => [v.slug, v.open, v.opensAt, v.closesAt, v.host?.avatarId, v.host?.name,
          (v.host?.media || []).map(plain).join(',')].join(':')).join('|');
        ws.setVenueRegistry(Object.fromEntries(list.map((v) => [v.slug, v])), registryKey(list), live);
        tick();
        if (res.added.length || res.removed.length || res.skipped.length) {
          console.log(`[venues] ${list.length} live (${why})`
            + (res.added.length ? ` · standing now: ${res.added.join(', ')}` : '')
            + (res.removed.length ? ` · retired: ${res.removed.join(', ')}` : '')
            + (res.skipped.length ? ` · NOT BUILT: ${res.skipped.join('; ')}` : ''));
        }
        // ?enter=<slug> for a registry venue. App's own ?enter= runs at boot,
        // before any registry room exists, so it cannot find one — this is the
        // same deep link, honoured once the room does.
        if (!entered.current) {
          entered.current = true;
          const want = new URLSearchParams(window.location.search).get('enter');
          // After the city's first frame, as App's ?enter= (2026-11-01): a venue that is
          // not sited unmounts the city, and the landing gate opens on that frame.
          if (want && ROOMS[want]?.registry && !ws.interior) {
            const go = () => setTimeout(() => { if (!useWorldStore.getState().interior) useWorldStore.getState().enterInterior(want); }, 400);
            if (bootState().marks.has('frame')) go();
            else { const off = subscribeBoot(() => { if (bootState().marks.has('frame')) { off(); go(); } }); }
          }
        }
      } catch (err) {
        // Missing (404 before the API ships it) or failing: keep what we had.
        if (!listRef.current.length && why === 'boot') console.log(`[venues] no registry (${err?.message || err}) — the city's own venues only`);
      } finally { busy.current = false; }
    };

    load('boot');
    const poll = setInterval(() => load('poll'), POLL_MS);
    const clock = setInterval(tick, TICK_MS);

    // A BUILD SHOWS UP IN THE CITY FIRST. The snapshot's places carry every
    // registry venue as `anchor: <slug>`; one this World has no room for means a
    // venue went live since the last read, so read again rather than wait out
    // the poll. Debounced: snapshots arrive in bursts.
    let lastPlaces = '';
    let soon = null;
    const unsub = useCityStore.subscribe((s) => {
      const places = s.snap?.places;
      if (!places) return;
      const key = places.filter((p) => p.anchor && !(ROOMS[p.anchor] && !ROOMS[p.anchor].registry))
        .map((p) => p.anchor).sort().join(',');
      if (key === lastPlaces) return;
      lastPlaces = key;
      const named = key ? key.split(',') : [];
      const known = new Set(listRef.current.map((v) => v.slug));
      const unknown = named.some((k) => !known.has(k));
      const gone = [...known].some((k) => !named.includes(k));
      if (unknown || gone) { clearTimeout(soon); soon = setTimeout(() => load('city changed'), 1500); }
    });

    return () => { dead = true; clearInterval(poll); clearInterval(clock); clearTimeout(soon); unsub(); };
  }, []);

  return null;
}
